// Collection and search pages: automatic loading, then a "Load more" button
// (sections/main-collection-product-grid, sections/main-search; markup in
// snippets/af-load-more.liquid).
//
// Baymard's recommended hybrid: the next page loads by itself as the shopper
// nears the bottom until autoPages() pages are showing (2 on desktop, so 72
// products on collections and 48 on search; 1 on phones), then a "Load more" button takes over, with a
// "Showing 72 of 234" count and a thin progress line. The footer stays
// reachable and the list grows in clear chunks.
//
// Progressive enhancement: the normal numbered pagination is still rendered in
// the HTML with real ?page=N links, so search engines crawl every page exactly
// as before. With JavaScript on, the page numbers are hidden.
//
// Speed: Shopify takes 1.5 to 2.5 s to render a page of 36 cards, so the next
// page is always fetched in the background as soon as the previous one lands.
//
// Browser history is never touched: however many pages are loaded, Back
// leaves the collection for the previous page. Separately, the number of pages
// loaded and the scroll position are kept in sessionStorage per collection URL
// (filters and sort included), so coming BACK to the collection from a product
// restores the same products and the same spot. When the browser restores the
// page from its back/forward cache nothing is needed.
//
// Filters and sorting: Dawn's facets.js replaces #ProductGridContainer; the
// observer below notices and starts again from the new first page.
(function () {
  if (!('IntersectionObserver' in window) || !window.fetch) return;

  var STORE_PREFIX = 'afInfinite:';
  var LOOKAHEAD = '0px 0px 900px 0px'; // start loading well before the bottom
  var MOBILE = window.matchMedia('(max-width: 749px)');
  var state = null;

  function autoPages() {
    return MOBILE.matches ? 1 : 2;
  }

  function storeKey() {
    return STORE_PREFIX + location.pathname + location.search;
  }
  function readStore() {
    try { return JSON.parse(sessionStorage.getItem(storeKey()) || 'null'); } catch (e) { return null; }
  }
  function writeStore(data) {
    try { sessionStorage.setItem(storeKey(), JSON.stringify(data)); } catch (e) {}
  }

  function cleanUrl(url) {
    var u = new URL(url, location.origin);
    u.searchParams.delete('section_id');
    return u;
  }

  // Reveal new cards with the theme's slide-in, cascading like the first page.
  var revealer = new IntersectionObserver(function (entries, obs) {
    var order = 0;
    entries.forEach(function (entry) {
      if (!entry.isIntersecting) return;
      entry.target.style.setProperty('--animation-order', order++);
      entry.target.classList.remove('scroll-trigger--offscreen');
      obs.unobserve(entry.target);
    });
  }, { rootMargin: '0px 0px -40px 0px' });

  function setLoading(on) {
    var spinner = state.box.querySelector('.loading__spinner');
    if (spinner) spinner.classList.toggle('hidden', !on);
    state.box.classList.toggle('af-infinite--loading', on);
    var btn = state.box.querySelector('.af-loadmore__btn');
    if (btn) btn.setAttribute('aria-busy', on ? 'true' : 'false');
  }

  function announce(text) {
    var live = state.box.querySelector('.af-infinite__status');
    if (live) live.textContent = text;
  }

  // Count, progress line and button visibility.
  function updateUi() {
    var s = state;
    var ui = s.box.querySelector('.af-loadmore');
    if (!ui) return;
    var shown = s.grid.children.length;
    var total = Math.max(s.total || shown, shown);
    var count = ui.querySelector('.af-loadmore__count');
    var bar = ui.querySelector('.af-loadmore__bar span');
    var btn = ui.querySelector('.af-loadmore__btn');
    var buttonPhase = !s.done && s.page >= autoPages();
    ui.hidden = !(buttonPhase || s.done);
    if (count) count.textContent = s.done ? 'Showing all ' + total : 'Showing ' + shown + ' of ' + total;
    if (bar) bar.style.width = (total ? Math.min(100, (shown / total) * 100) : 100) + '%';
    if (btn) btn.hidden = !buttonPhase;
  }

  function finish() {
    state.done = true;
    state.sentinel.disconnect();
    setLoading(false);
    updateUi();
    // Arrived part way through (e.g. from a search result for ?page=3):
    // show the page numbers again at the end so earlier pages stay reachable.
    if (state.startPage > 1) state.wrapper.classList.remove('af-infinite-on');
  }

  function fetchPage(s, nextUrl) {
    if (s.prefetch && s.prefetch.url === nextUrl) return s.prefetch.promise;
    var url = cleanUrl(nextUrl);
    url.searchParams.set('section_id', s.sectionId);
    var promise = fetch(url.toString(), { credentials: 'same-origin' }).then(function (r) {
      if (!r.ok) throw new Error('HTTP ' + r.status);
      return r.text();
    });
    promise.catch(function () {});
    s.prefetch = { url: nextUrl, promise: promise };
    return promise;
  }

  function loadNext() {
    if (!state || state.loading || state.done || !state.nextUrl) return Promise.resolve(false);
    state.loading = true;
    setLoading(true);
    var s = state;
    return fetchPage(s, s.nextUrl)
      .then(function (html) {
        if (state !== s) return false; // grid was replaced (filter/sort) meanwhile
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var items = doc.querySelectorAll('#product-grid > li, #product-grid > ul > li');
        var frag = document.createDocumentFragment();
        items.forEach(function (li) {
          if (li.classList.contains('scroll-trigger')) li.classList.add('scroll-trigger--offscreen');
          frag.appendChild(document.importNode(li, true));
        });
        var added = Array.prototype.slice.call(frag.children);
        s.grid.appendChild(frag);
        added.forEach(function (li) {
          if (li.classList.contains('scroll-trigger')) revealer.observe(li);
        });
        var nextBox = doc.querySelector('.af-infinite');
        s.page += 1;
        s.nextUrl = nextBox && nextBox.dataset.nextUrl ? nextBox.dataset.nextUrl : null;
        writeStore({ pages: s.page, start: s.startPage });
        announce(added.length + ' more products loaded, showing ' + s.grid.children.length + ' of ' + Math.max(s.total, s.grid.children.length));
        s.loading = false;
        setLoading(false);
        if (!s.nextUrl) finish();
        else {
          updateUi();
          fetchPage(s, s.nextUrl); // warm the following page
          if (s.inView && s.page < autoPages()) loadNext(); // still at the bottom (tall screens)
        }
        return true;
      })
      .catch(function () {
        // Network trouble: fall back to the normal page numbers.
        s.loading = false;
        setLoading(false);
        s.sentinel.disconnect();
        var ui = s.box.querySelector('.af-loadmore');
        if (ui) ui.hidden = true;
        s.wrapper.classList.remove('af-infinite-on');
        return false;
      });
  }

  function init() {
    var container = document.getElementById('ProductGridContainer');
    var box = container && container.querySelector('.af-infinite');
    var grid = container && container.querySelector('#product-grid');
    // Collections: #product-grid is the list itself. Search: it wraps the list.
    if (grid && grid.tagName !== 'UL') grid = grid.querySelector('ul.product-grid');
    if (state && state.sentinel) state.sentinel.disconnect();
    state = null;
    if (!box || !grid) return;

    var wrapper = box.closest('.collection') || container;
    state = {
      box: box,
      grid: grid,
      wrapper: wrapper,
      sectionId: box.dataset.sectionId,
      total: parseInt(box.dataset.total, 10) || 0,
      page: parseInt(box.dataset.page, 10) || 1,
      startPage: parseInt(box.dataset.page, 10) || 1,
      nextUrl: box.dataset.nextUrl || null,
      loading: false,
      done: false,
      inView: false
    };
    if (!state.nextUrl) return;
    wrapper.classList.add('af-infinite-on');

    var s = state;
    var btn = box.querySelector('.af-loadmore__btn');
    if (btn) btn.addEventListener('click', function () { if (state === s) loadNext(); });
    updateUi();

    s.sentinel = new IntersectionObserver(function (entries) {
      s.inView = entries[0].isIntersecting;
      if (s.inView && s.page < autoPages()) loadNext();
    }, { rootMargin: LOOKAHEAD });
    s.sentinel.observe(box);
    // Warm page 2 once the page has settled.
    var warm = function () { if (state === s && !s.done) fetchPage(s, s.nextUrl); };
    if ('requestIdleCallback' in window) requestIdleCallback(warm, { timeout: 3000 });
    else setTimeout(warm, 1500);
  }

  // Back to this collection from a product: reload the pages the shopper had,
  // then scroll back to the same spot.
  function restore() {
    var nav = performance.getEntriesByType && performance.getEntriesByType('navigation')[0];
    var saved = readStore();
    if (!nav || nav.type !== 'back_forward' || !saved || !state || saved.pages <= state.page) {
      if (nav && nav.type !== 'back_forward') writeStore({ pages: state ? state.page : 1, start: state ? state.startPage : 1 });
      return;
    }
    var y = saved.y || 0;
    if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
    (function step() {
      if (!state || state.page >= saved.pages || state.done) {
        window.scrollTo(0, y);
        return;
      }
      loadNext().then(function (ok) { if (ok) step(); else window.scrollTo(0, y); });
    })();
  }

  window.addEventListener('pagehide', function () {
    if (!state) return;
    var saved = readStore() || {};
    saved.pages = state.page;
    saved.start = state.startPage;
    saved.y = window.scrollY;
    writeStore(saved);
  });

  function start() {
    init();
    restore();
    var container = document.getElementById('ProductGridContainer');
    if (container) {
      new MutationObserver(function (records) {
        // Only a full replacement by facets.js (its direct children change);
        // our own appends happen deeper inside #product-grid.
        if (records.some(function (r) { return r.target === container; })) init();
      }).observe(container, { childList: true });
    }
  }

  // Small hook for testing from the console: afInfinite.loadNext(), afInfinite.state()
  window.afInfinite = {
    loadNext: loadNext,
    state: function () { return state && { page: state.page, nextUrl: state.nextUrl, done: state.done, loading: state.loading, total: state.total, auto: autoPages() }; }
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
